<template>
  <div class="beds-page">

    <!-- Page header -->
    <div class="page-header">
      <button class="back-btn" onclick="window.history.back()"><i class="ft-arrow-left"></i></button>
      <div class="header-info">
        <span class="page-title">{{ wardName || 'Patients' }}</span>
        <span class="page-subtitle"><i class="ft-calendar"></i> {{ date }}</span>
      </div>
    </div>

    <!-- Loading -->
    <div class="loading-state" v-if="loading">
      <div class="spinner"></div>
      <span>Loading patients…</span>
    </div>

    <!-- Search + count bar -->
    <div class="section-pad" v-if="!loading && beds.length > 0">
      <div class="summary-bar">
        <span class="bed-count-badge">{{ filteredBeds.length }} patient{{ filteredBeds.length !== 1 ? 's' : '' }}</span>
        <div class="search-wrap">
          <i class="ft-search search-icon"></i>
          <input
            class="search-input"
            type="text"
            v-model="searchQuery"
            placeholder="Search name or bed…"
          >
          <button class="search-clear" v-if="searchQuery" @click="searchQuery = ''">
            <i class="ft-x"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- No search results -->
    <div class="empty-state" v-if="!loading && beds.length > 0 && filteredBeds.length === 0">
      <i class="ft-search"></i>
      <p>No patients match <strong>"{{ searchQuery }}"</strong></p>
      <button class="clear-search-btn" @click="searchQuery = ''">Clear search</button>
    </div>

    <!-- Empty ward -->
    <div class="empty-state" v-if="!loading && beds.length === 0">
      <i class="ft-inbox"></i>
      <p>No patients found in this ward.</p>
    </div>

    <!-- Bed cards -->
    <div class="section-pad" v-if="!loading && filteredBeds.length > 0">
      <div class="beds-grid">
        <div class="patient-block" v-for="bed in filteredBeds" :key="bed.bed_id">
          <a href="javascript:void(0)" class="bed-card" @click="saveBed(bed); generateLink(bed)">

            <div class="bed-card-top">
              <div class="patient-avatar">{{ bed.patient_name ? bed.patient_name.charAt(0).toUpperCase() : '?' }}</div>
              <div class="bed-info">
                <div class="bed-number">Bed {{ bed.bed_code }}</div>
                <div class="patient-name">{{ bed.patient_name }}</div>
              </div>
              <button class="edit-bed-btn" @click.stop="editBed(bed)" title="Edit patient">
                <i class="ft-more-vertical"></i>
              </button>
            </div>

            <div class="bed-card-meta">
              <span class="menu-pill"><i class="ft-bookmark"></i> {{ bed.menu_type_name }}</span>
              <span class="allergy-pill" v-if="bed.allergies && bed.allergies != ''">
                <i class="ft-alert-triangle"></i> {{ allergies(bed.allergies) }}
              </span>
            </div>

            <div class="important-info" v-if="bed.important_info != null && bed.important_info != ''">
              <i class="ft-alert-circle"></i> {{ bed.important_info }}
            </div>

            <div class="order-breakdown">
              <div class="breakdown-col">
                <div class="breakdown-day">Today</div>
                <div class="breakdown-dots">
                  <span class="meal-dot dot-b" v-if="bed.order_breakdown.today.breakfast">B</span>
                  <span class="meal-dot dot-l" v-if="bed.order_breakdown.today.lunch">L</span>
                  <span class="meal-dot dot-d" v-if="bed.order_breakdown.today.dinner">D</span>
                  <span class="meal-none" v-if="!bed.order_breakdown.today.breakfast && !bed.order_breakdown.today.lunch && !bed.order_breakdown.today.dinner">—</span>
                </div>
              </div>
              <div class="breakdown-divider"></div>
              <div class="breakdown-col">
                <div class="breakdown-day">Tomorrow</div>
                <div class="breakdown-dots">
                  <span class="meal-dot dot-b" v-if="bed.order_breakdown.tomorrow.breakfast">B</span>
                  <span class="meal-dot dot-l" v-if="bed.order_breakdown.tomorrow.lunch">L</span>
                  <span class="meal-dot dot-d" v-if="bed.order_breakdown.tomorrow.dinner">D</span>
                  <span class="meal-none" v-if="!bed.order_breakdown.tomorrow.breakfast && !bed.order_breakdown.tomorrow.lunch && !bed.order_breakdown.tomorrow.dinner">—</span>
                </div>
              </div>
            </div>

            <div class="place-order-cta">
              <i class="ft-plus-circle"></i> Place Order
            </div>

          </a>
        </div>
      </div>
    </div>

    <!-- Modal: Edit Patient -->
    <div class="modal" tabindex="-1" role="dialog" id="editpateint-modal">
      <div class="modal-dialog" role="document">
        <div class="modal-content app-modal">
          <div class="app-modal-header">
            <span>Edit Patient</span>
            <button type="button" class="modal-close-btn" data-dismiss="modal"><i class="ft-x"></i></button>
          </div>
          <div class="modal-body"><p class="modal-hint">Select a patient action:</p></div>
          <div class="app-modal-footer">
            <button type="button" class="modal-btn btn-discharge" @click="discharge()" data-dismiss="modal"><i class="ft-log-out"></i> Discharge</button>
            <button type="button" class="modal-btn btn-transfer" @click="transfer()" data-dismiss="modal"><i class="ft-repeat"></i> Transfer</button>
            <button type="button" class="modal-btn btn-edit" @click="editMenu()" data-dismiss="modal"><i class="ft-edit"></i> Edit</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Discharge -->
    <div class="modal" tabindex="-1" role="dialog" id="discharge-modal">
      <div class="modal-dialog" role="document">
        <div class="modal-content app-modal">
          <div class="app-modal-header app-modal-header--danger">
            <span>Discharge Patient</span>
            <button type="button" class="modal-close-btn" data-dismiss="modal"><i class="ft-x"></i></button>
          </div>
          <div class="modal-body"><p class="modal-hint">Are you sure? The bed will become empty and future orders cancelled.</p></div>
          <div class="app-modal-footer">
            <button type="button" class="modal-btn btn-discharge" @click="dischargeNow()" data-dismiss="modal"><i class="ft-log-out"></i> Confirm Discharge</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Transfer -->
    <div class="modal" tabindex="-1" role="dialog" id="transfer-modal">
      <div class="modal-dialog" role="document">
        <div class="modal-content app-modal">
          <div class="app-modal-header">
            <span>Transfer Patient</span>
            <button type="button" class="modal-close-btn" data-dismiss="modal"><i class="ft-x"></i></button>
          </div>
          <div class="modal-body">
            <div class="modal-field">
              <label class="select-label">New Ward</label>
              <div class="select-wrap">
                <select v-model="transfer_ward_id">
                  <option value="">Select a ward</option>
                  <option v-for="w in allWards" :key="w.ward_id" :value="w.ward_id">{{ w.ward_name }}</option>
                </select>
                <i class="ft-chevron-down select-arrow"></i>
              </div>
            </div>
            <div class="modal-field">
              <label class="select-label">New Bed</label>
              <div class="select-wrap">
                <select v-model="transfer_bed_id">
                  <option value="">Select a bed</option>
                  <option v-for="b in beds" :key="b.bed_id" :value="b.bed_id">{{ b.bed_code }}</option>
                </select>
                <i class="ft-chevron-down select-arrow"></i>
              </div>
            </div>
          </div>
          <div class="app-modal-footer">
            <button type="button" class="modal-btn btn-edit" @click="transferNow()" data-dismiss="modal"><i class="ft-check"></i> Save Transfer</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: Edit Menu -->
    <div class="modal" tabindex="-1" role="dialog" id="edit-modal">
      <div class="modal-dialog" role="document">
        <div class="modal-content app-modal">
          <div class="app-modal-header">
            <span>Edit Menu Type</span>
            <button type="button" class="modal-close-btn" data-dismiss="modal"><i class="ft-x"></i></button>
          </div>
          <div class="modal-body">
            <div class="modal-field">
              <label class="select-label">Patient</label>
              <input class="modal-input" type="text" :value="activeBed.patient_name" readonly>
            </div>
            <div class="modal-field">
              <label class="select-label">Menu</label>
              <div class="select-wrap">
                <select v-model="edit_menu_type">
                  <option value="">Select menu</option>
                  <option v-for="menu in menu_types" :key="menu.id" :value="menu.id">{{ menu.name }}</option>
                </select>
                <i class="ft-chevron-down select-arrow"></i>
              </div>
            </div>
          </div>
          <div class="app-modal-footer">
            <button type="button" class="modal-btn btn-edit" @click="saveMenu()" data-dismiss="modal"><i class="ft-check"></i> Save Changes</button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import moment from 'moment'
import swal from 'sweetalert'

export default {
  name: 'beds',
  data: function () {
    return {
      beds: [],
      activeBed: {},
      date: moment().format('DD MMMM YYYY'),
      loading: true,
      searchQuery: '',
      wardName: '',
      allWards: [],
      menu_types: [],
      transfer_ward_id: '',
      transfer_bed_id: '',
      edit_menu_type: ''
    }
  },
  computed: {
    filteredBeds: function () {
      var q = this.searchQuery.trim().toLowerCase()
      if (!q) return this.beds
      return this.beds.filter(function (b) {
        return (b.patient_name || '').toLowerCase().includes(q) ||
               (b.bed_code || '').toLowerCase().includes(q)
      })
    }
  },
  mounted: function () {
    this.$store.commit('SET_LAYOUT', 'AppLayout')
    var user = JSON.parse(window.localStorage.getItem('user'))
    this.allWards = user.merchant.wards || []

    var wardId = this.$route.params.wardId
    var ward = this.allWards.find(function (w) { return String(w.ward_id) === String(wardId) })
    if (ward) {
      this.wardName = ward.ward_name
      window.localStorage.setItem('ward', JSON.stringify(ward))
    }

    this.fetchBeds(wardId)
  },
  methods: {
    fetchBeds: function (wardId) {
      var that = this
      that.loading = true
      $.ajax({
        beforeSend: function (xhr) {
          xhr.setRequestHeader('Authorization', window.localStorage.getItem('token'))
        },
        url: this.$config.apiUrl + 'medicater/beds/' + wardId + '?date=' + moment().format('YYYY-MM-DD') + '&ward_id=' + wardId,
        type: 'get',
        success: function (data) {
          that.beds = data.data
          that.loading = false
        },
        error: function (data) {
          that.loading = false
          swal({ text: data.responseJSON ? data.responseJSON.message : 'Could not load patients.', icon: 'info', button: 'Ok' })
        }
      })
    },
    allergies: function (bedAllergies) {
      return bedAllergies.map(function (a) { return a.name }).join(', ')
    },
    saveBed: function (bed) {
      window.localStorage.setItem('bed', JSON.stringify(bed))
      window.localStorage.setItem('menu_type_id', bed.menu_type_id)
    },
    generateLink: function (bed) {
      this.saveBed(bed)
      this.$router.push('/preorder/' + bed.ward_id + '/' + bed.bed_id)
    },
    editBed: function (bed) {
      this.activeBed = bed
      $('#editpateint-modal').modal('show')
    },
    discharge: function () { $('#discharge-modal').modal('show') },
    transfer: function () { $('#transfer-modal').modal('show') },
    editMenu: function () { $('#edit-modal').modal('show') },
    dischargeNow: function () {},
    transferNow: function () {},
    saveMenu: function () {}
  }
}
</script>

<style scoped>
.beds-page {
  min-height: 100vh;
  background: #f0f4f8;
  padding-bottom: 40px;
}

/* ── Page header ── */
.page-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 30px 20px 20px;
  background: linear-gradient(135deg, #1a587b 0%, #0f3d5c 100%);
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 10;
  margin-top: 3.2rem;
  border-radius: 0;
  box-shadow: 0 8px 32px rgba(15, 61, 92, 0.35);
}
.back-btn {
  width: 36px; height: 36px; border-radius: 10px;
  background: #fb5653; border: 1px solid rgba(255,255,255,0.2);
  color: #fff; display: flex; align-items: center; justify-content: center;
  font-size: 1rem; cursor: pointer; flex-shrink: 0; transition: background 0.15s;
}
.back-btn:hover { background: #e03e3b; }
.header-info { flex: 1; display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.page-title { font-size: 1.05rem; font-weight: 700; color: #fff; }
.page-subtitle { font-size: 0.73rem; color: rgba(255,255,255,0.65); font-weight: 500; display: flex; align-items: center; gap: 4px; }
.page-subtitle i { font-size: 0.65rem; }

/* ── Section pad ── */
.section-pad { padding:90px 16px 0; }
.section-pad + .section-pad { padding-top: 14px; }

/* ── Loading ── */
.loading-state { display: flex; flex-direction: column; align-items: center; gap: 14px; padding: 80px 16px; color: #94a3b8; font-size: 0.875rem; }
.spinner { position: relative; width: 38px; height: 38px; border-radius: 50%; border: 3px solid transparent; border-top-color: #1a587b; border-left-color: #1a587b; animation: spin 0.9s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
.spinner::after { content: ''; position: absolute; inset: 5px; border-radius: 50%; border: 2.5px solid transparent; border-top-color: rgba(26,88,123,0.35); border-left-color: rgba(26,88,123,0.35); animation: spin 1.8s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite reverse; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Summary bar ── */
.summary-bar { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.bed-count-badge { font-size: 0.7rem; font-weight: 700; color: #1a587b; background: #e0f2fe; padding: 3px 10px; border-radius: 999px; white-space: nowrap; }
.search-wrap { position: relative; display: flex; align-items: center; flex: 1; min-width: 180px; }
.search-icon { position: absolute; left: 13px; color: #94a3b8; font-size: 0.875rem; pointer-events: none; }
.search-input { width: 100%; padding: 10px 36px 10px 36px; border: 1.5px solid #e2e8f0; border-radius: 10px; font-size: 0.875rem; color: #0f172a; background: #fff; outline: none; transition: border-color 0.15s, box-shadow 0.15s; box-sizing: border-box; }
.search-input:focus { border-color: #1a587b; box-shadow: 0 0 0 3px rgba(26,88,123,0.1); }
.search-input::placeholder { color: #b0bcc8; }
.search-clear { position: absolute; right: 10px; width: 22px; height: 22px; border: none; background: #e2e8f0; border-radius: 50%; color: #64748b; display: flex; align-items: center; justify-content: center; font-size: 0.65rem; cursor: pointer; }

/* ── Empty state ── */
.empty-state { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 80px 32px; text-align: center; color: #94a3b8; }
.empty-state i { font-size: 2rem; }
.empty-state p { font-size: 0.875rem; margin: 0; }
.clear-search-btn { padding: 8px 16px; border-radius: 8px; border: 1.5px solid #e2e8f0; background: #fff; color: #1a587b; font-size: 0.8rem; font-weight: 600; cursor: pointer; }

/* ── Beds grid ── */
.beds-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
@media (min-width: 480px) { .beds-grid { grid-template-columns: repeat(2, 1fr); } }
@media (min-width: 768px) { .beds-grid { grid-template-columns: repeat(3, 1fr); } }
@media (min-width: 1024px) { .beds-grid { grid-template-columns: repeat(4, 1fr); } }

/* patient-block is a transparent layout wrapper — bed-card becomes the grid item */
.patient-block { display: contents; }

/* ── Bed card ── */
.bed-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.07);
  border: 1px solid #e8edf2;
  text-decoration: none !important;
  transition: box-shadow 0.2s, transform 0.15s;
}
.bed-card:hover { box-shadow: 0 6px 20px rgba(0,0,0,0.12); transform: translateY(-2px); }
.bed-card:active { transform: scale(0.98); }
/* push the CTA to the bottom of every card */
.place-order-cta { margin-top: auto; display: -webkit-box; display: -ms-flexbox; display: flex; -webkit-box-align: center; -ms-flex-align: center; align-items: center; -webkit-box-pack: center; -ms-flex-pack: center; justify-content: center; gap: 6px; padding: 10px 16px; background: #124564; color: #fff; font-size: 0.8rem; font-weight: 700; letter-spacing: 0.3px; border-top: none; border-radius: 15px; margin-bottom: 1em; max-width: 90%; align-self: center; width: 100%; cursor: pointer; transition: opacity 0.15s; }

.bed-card-top { display: flex; align-items: center; gap: 10px; padding: 14px 14px 10px; }
.patient-avatar { width: 36px; height: 36px; border-radius: 10px; background: linear-gradient(135deg,#1a587b,#0f3d5c); display: flex; align-items: center; justify-content: center; font-size: 0.95rem; font-weight: 700; color: #fff; flex-shrink: 0; }
.bed-info { min-width: 0; flex: 1; }
.bed-number { font-size: 0.68rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.8px; }
.patient-name { font-size: 0.82rem; font-weight: 600; color: #0f172a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.edit-bed-btn { width: 28px; height: 28px; border-radius: 8px; border: 1px solid #e2e8f0; background: #f8fafc; color: #64748b; display: flex; align-items: center; justify-content: center; font-size: 0.8rem; cursor: pointer; flex-shrink: 0; transition: background 0.15s; }
.edit-bed-btn:hover { background: #e0f2fe; color: #1a587b; }

.bed-card-meta { display: flex; flex-wrap: wrap; gap: 5px; padding: 0 14px 10px; }
.menu-pill { font-size: 0.65rem; font-weight: 600; padding: 3px 8px; border-radius: 999px; background: #e0f2fe; color: #0369a1; display: flex; align-items: center; gap: 3px; }
.allergy-pill { font-size: 0.65rem; font-weight: 600; padding: 3px 8px; border-radius: 999px; background: #fef3c7; color: #92400e; display: flex; align-items: center; gap: 3px; }

.important-info { display: flex; align-items: center; gap: 6px; padding: 6px 14px; background: #fff7ed; font-size: 0.72rem; color: #c2410c; font-weight: 500; }

.order-breakdown { display: flex; align-items: stretch; border-top: 1px solid #f1f5f9; background: #f8fafc; }
.breakdown-col { flex: 1; padding: 8px 12px; display: flex; flex-direction: column; gap: 5px; }
.breakdown-divider { width: 1px; background: #e2e8f0; margin: 6px 0; }
.breakdown-day { font-size: 0.62rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.8px; color: #94a3b8; }
.breakdown-dots { display: flex; gap: 4px; flex-wrap: wrap; }
.meal-dot { width: 20px; height: 20px; border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 0.6rem; font-weight: 700; color: #fff; }
.dot-b { background: #0ea5e9; }
.dot-l { background: #10b981; }
.dot-d { background: #8b5cf6; }
.meal-none { font-size: 0.75rem; color: #cbd5e1; font-weight: 600; }


/* ── Modals ── */
.app-modal { border-radius: 16px; overflow: hidden; border: none; }
.app-modal-header { background: linear-gradient(135deg,#1a587b,#0f3d5c); padding: 16px 20px; display: flex; align-items: center; justify-content: space-between; }
.app-modal-header span { font-size: 0.95rem; font-weight: 700; color: #fff; }
.app-modal-header--danger { background: linear-gradient(135deg,#ef4444,#b91c1c); }
.modal-close-btn { width: 28px; height: 28px; border-radius: 8px; border: none; background: rgba(255,255,255,0.2); color: #fff; display: flex; align-items: center; justify-content: center; cursor: pointer; }
.modal-hint { font-size: 0.875rem; color: #475569; margin: 0; }
.app-modal-footer { padding: 14px 16px; display: flex; gap: 8px; flex-wrap: wrap; border-top: 1px solid #f1f5f9; }
.modal-btn { flex: 1; padding: 10px 14px; border: none; border-radius: 10px; font-size: 0.82rem; font-weight: 700; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px; transition: opacity 0.15s; }
.btn-discharge { background: #fee2e2; color: #dc2626; }
.btn-transfer { background: #fef3c7; color: #92400e; }
.btn-edit { background: linear-gradient(135deg,#1a587b,#0f3d5c); color: #fff; }
.modal-field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; }
.select-label { font-size: 0.75rem; font-weight: 600; color: #475569; }
.select-wrap { position: relative; }
.select-wrap select { width: 100%; appearance: none; -webkit-appearance: none; padding: 10px 34px 10px 12px; border: 1.5px solid #e2e8f0; border-radius: 9px; font-size: 0.875rem; color: #0f172a; background: #f8fafc; outline: none; }
.ft-chevron-down.select-arrow { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); color: #94a3b8; pointer-events: none; font-size: 0.8rem; }
.modal-input { padding: 10px 12px; border: 1.5px solid #e2e8f0; border-radius: 9px; font-size: 0.875rem; color: #0f172a; background: #f1f5f9; width: 100%; box-sizing: border-box; outline: none; }
</style>
